import type { Image } from '../../types/media'
import type { Favicon } from '../../types/theme'

/**
 * Resolves a theme asset `src` to the URL the browser should load.
 *
 * Theme files declare assets with **brand-relative keys** like
 * `spilnu/logo/primary.png`. The generated `#theme-assets` template maps
 * every file under `themes/<brand>/assets/**` to the public URL it is
 * served from and exposes the map as `themeAssetUrls`.
 *
 * Any value that already looks like a URL — absolute path, protocol, or
 * `data:` — passes through unchanged. This lets consumers override slots
 * in `site.logo` / `site.favicon` with CDN URLs or data URIs without
 * having to register them as theme assets.
 *
 * Unknown keys resolve to `undefined` so callers can treat a missing asset
 * the same as an unset slot. In dev we warn so typos surface immediately.
 */
export function resolveThemeAssetSrc(
    src: string | undefined,
    assetUrls: Record<string, string>,
): string | undefined {
    if (!src) return undefined
    if (isExternalUrl(src)) return src

    const resolved = assetUrls[src]
    if (!resolved) {
        if (import.meta.dev) {
            console.warn(`[@spilnu/core theme] Unknown theme asset key: ${JSON.stringify(src)}. Known keys: ${Object.keys(assetUrls).join(', ')}`)
        }
        return undefined
    }
    return resolved
}

/**
 * Applies `resolveThemeAssetSrc` to an `Image` and returns the same shape
 * with `src` rewritten to the resolved URL, or `undefined` when the
 * source can't be resolved.
 */
export function resolveThemeAssetImage(
    image: Image | undefined,
    assetUrls: Record<string, string>,
): Image | undefined {
    if (!image) return undefined
    const src = resolveThemeAssetSrc(image.src, assetUrls)
    if (!src) return undefined
    return { ...image, src }
}

function isExternalUrl(src: string): boolean {
    return src.startsWith('/')
        || src.startsWith('http://')
        || src.startsWith('https://')
        || src.startsWith('data:')
}

/**
 * Resolves every slot of a `Favicon` bundle through `themeAssetUrls`.
 * Slots whose key can't be resolved drop out (same contract as
 * `resolveThemeAssetImage`).
 */
export function resolveFavicon(
    favicon: Favicon,
    assetUrls: Record<string, string>,
): Favicon {
    return {
        svg: resolveThemeAssetImage(favicon.svg, assetUrls),
        ico: resolveThemeAssetImage(favicon.ico, assetUrls),
        png: resolveThemeAssetImage(favicon.png, assetUrls),
        appleTouch: resolveThemeAssetImage(favicon.appleTouch, assetUrls),
    }
}
